import type { OrganizationRole } from '@/features/organizations/api/schemas';
import { canManageOrganizationApiKeys, canManageOrganizationBilling } from '@/features/organizations/permissions';
import type { NavigationItem, NavigationSubItem } from './nav-types';

export const ZERO_ORG_NAVIGATION_ROUTES = new Set(['/organizations']);

export const NAV_ITEMS: NavigationItem[] = [
    {
        title: 'home',
        href: '/home',
        icon: 'Home',
        section: 'main'
    },
    {
        title: 'publishing',
        href: '/publishing',
        icon: 'Send',
        section: 'main'
    },
    {
        title: 'socialAccounts',
        href: '/social-accounts',
        icon: 'AtSign',
        section: 'main'
    },
    {
        title: 'insights',
        href: '/insights',
        icon: 'BarChart3',
        section: 'main',
        subItems: [
            { title: 'content', href: '/insights', hash: 'content', icon: 'LayoutGrid' },
            { title: 'audience', href: '/insights', hash: 'audience', icon: 'Users' },
            { title: 'advancedQuery', href: '/insights', hash: 'query', icon: 'Terminal' }
        ]
    },
    {
        title: 'apiKeys',
        href: '/api-keys',
        icon: 'KeyRound',
        section: 'main',
        isVisible: canManageOrganizationApiKeys
    },
    {
        title: 'organizations',
        href: '/organizations',
        icon: 'Building2',
        section: 'setup',
        activeMatchers: ['/organizations', '/members', '/invitations'],
        subItems: [
            { title: 'members', href: '/organizations', hash: 'members', icon: 'Users' },
            { title: 'invitations', href: '/organizations', hash: 'invitations', icon: 'Mail' }
        ]
    },
    {
        title: 'apiDocs',
        href: '/api-docs',
        icon: 'BookOpen',
        section: 'setup'
    },
    {
        title: 'settings',
        href: '/settings',
        icon: 'Settings',
        section: 'setup',
        isVisible: canManageOrganizationBilling
    }
];

function matchesPath(pathname: string, href: string, exact = false): boolean {
    if (exact) {
        return pathname === href;
    }

    return pathname === href || pathname.startsWith(`${href}/`);
}

export function isNavigationItemActive(item: NavigationItem, pathname: string): boolean {
    if (item.activeMatchers?.length) {
        return item.activeMatchers.some((matcher) => matchesPath(pathname, matcher));
    }

    return matchesPath(pathname, item.href, item.exact);
}

export function isNavigationSubItemActive(subItem: NavigationSubItem, pathname: string, hash: string): boolean {
    if (subItem.activeMatchers?.length) {
        return subItem.activeMatchers.some((matcher) => matchesPath(pathname, matcher));
    }

    if (subItem.hash) {
        const normalizedHash = hash.startsWith('#') ? hash.slice(1) : hash;
        return matchesPath(pathname, subItem.href) && normalizedHash === subItem.hash;
    }

    return matchesPath(pathname, subItem.href);
}

export function getVisibleNavItems(role: OrganizationRole | null, hasNoOrganizations = false): NavigationItem[] {
    return NAV_ITEMS.filter((item) => {
        if (hasNoOrganizations) {
            return ZERO_ORG_NAVIGATION_ROUTES.has(item.href);
        }

        return item.isVisible?.(role) ?? true;
    });
}

export function getNavigationSections(role: OrganizationRole | null, hasNoOrganizations = false) {
    const items = getVisibleNavItems(role, hasNoOrganizations);

    return [
        {
            title: 'main' as const,
            items: items.filter((item) => item.section === 'main')
        },
        {
            title: 'setup' as const,
            items: items.filter((item) => item.section === 'setup')
        }
    ].filter((section) => section.items.length > 0);
}

export function findNavigationItem(pathname: string): NavigationItem | null {
    return NAV_ITEMS.find((item) => isNavigationItemActive(item, pathname)) ?? null;
}
